import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { isNil } from 'lodash-es';

import i18n from 'portal-base/src/decorators/i18n';

import { NULL_PARAMETER } from 'ds-shared-lib/src/base/constants';

import { KiDsPwaComponent } from '../../base/ki-ds-pwa-component';
import nls from '../../locales/indexes.nls';
import { resolveAlarmState } from '../../services/alarming';
import { formatDateTime } from '../../utils/datetime';
import styles from './ki-ds-pwa-timeseries-list-item.css';
import '../ki-ds-pwa-alarm-bar/ki-ds-pwa-alarm-bar';

const ICON_MAP = {
    undefined: 'hmtmobile-chart-column',
    good: 'hmtmobile-check',
    warning: 'hmtmobile-circle-question',
    alarm: 'hmtmobile-triangle-exclamation',
};

const CLASS_NAME = 'ki-ds-pwa-timeseries-list-item';

@i18n(nls)
@customElement(CLASS_NAME)
export class KiDsPwaTimeseriesListItem extends KiDsPwaComponent {
    static styles = styles;

    _className = CLASS_NAME;

    static get properties() {
        return {
            timeseries: { type: Object },
            alarmStatus: { type: Object },
            alarmConfig: { type: Object },
            classificationTemplate: { type: Object },
            parameters: { type: Object },
            selected: { type: Boolean },
            selectable: { type: Boolean },
            _expanded: { type: Boolean, state: true },
        };
    }

    constructor() {
        super();
        this.timeseries = null;
        this.alarmStatus = null;
        this.alarmConfig = null;
        this.classificationTemplate = null;
        this.parameters = null;
        this.selected = false;
        this.selectable = true;
        this._expanded = false;
        this._longPressTriggered = false;
    }

    get _state() {
        if (!this.alarmStatus) return 'undefined';
        return resolveAlarmState(this.alarmStatus.level);
    }

    get _hasAlarmDetail() {
        return this.alarmConfig && this.alarmStatus?.eventDefinitionType;
    }

    // --- Event handlers ---

    _onClick(e) {
        if (!this.selectable) return;
        // On touch devices, touchend handles taps — ignore synthetic clicks
        if (this._longPressTriggered) {
            this._longPressTriggered = false;
            return;
        }
        this.dispatchEvent(
            new CustomEvent('ts-select', {
                detail: { tsId: this.timeseries.id, timeseries: this.timeseries },
                bubbles: true,
                composed: true,
            }),
        );
    }

    _onContextMenu(e) {
        if (!this.selectable) return;
        e.preventDefault();
        // contextmenu = right-click (desktop) or native long-press (mobile)
        this._longPressTriggered = true;
        this.dispatchEvent(
            new CustomEvent('ts-add', {
                detail: { tsId: this.timeseries.id, timeseries: this.timeseries },
                bubbles: true,
                composed: true,
            }),
        );
    }

    _onTouchEnd(e) {
        if (!this.selectable) return;
        if (e.composedPath().some((el) => el.classList?.contains('expand-toggle'))) return;
        if (this._longPressTriggered) {
            // Long press already handled via contextmenu — suppress click
            e.preventDefault();
            return;
        }
        // Short tap → select
        e.preventDefault();
        this.dispatchEvent(
            new CustomEvent('ts-select', {
                detail: { tsId: this.timeseries.id, timeseries: this.timeseries },
                bubbles: true,
                composed: true,
            }),
        );
    }

    _onTouchStart() {
        this._longPressTriggered = false;
    }

    _toggleExpand(e) {
        e.stopPropagation();
        this._expanded = !this._expanded;
    }

    render() {
        if (!this.timeseries) return nothing;

        const state = this._state;
        const itemClasses = [
            'item',
            this.selected ? 'selected' : '',
            state !== 'undefined' ? `status-${state}` : '',
        ]
            .filter(Boolean)
            .join(' ');

        return html`
            <div
                class=${itemClasses}
                data-tsid=${this.timeseries.id}
                @click=${this._onClick}
                @contextmenu=${this._onContextMenu}
                @touchstart=${this._onTouchStart}
                @touchend=${this._onTouchEnd}
            >
                ${this._renderStatusIcon(state)} ${this._renderBody()}
                ${this._hasAlarmDetail ? this._renderExpandToggle() : nothing}
            </div>
            ${this._hasAlarmDetail ? this._renderDetailPanel() : nothing}
        `;
    }

    _renderStatusIcon(state) {
        return html`
            <div class="status-icon icon-${state}">
                <i class="hmtmobile ${ICON_MAP[state]}"></i>
            </div>
        `;
    }

    _renderBody() {
        const ts = this.timeseries;
        const value = this._formatValue(ts);
        const unit = ts.unit && ts.unit !== '--' ? ts.unit : '';
        const date = ts.dataTo ? this._formatDate(ts.dataTo) : '---';
        const variable = isNil(ts.variable) || ts.variable === NULL_PARAMETER
            ? this._getLabel('none')
            : ts.variable;

        return html`
            <div class="body">
                <div class="name">
                    ${variable} ${ts.name}
                </div>
                <div class="meta">
                    <span class="meta-value">${value} ${unit}</span>
                    <span class="meta-date">${date}</span>
                </div>
            </div>
        `;
    }

    _renderExpandToggle() {
        return html`
            <div class="expand-toggle" @click=${this._toggleExpand}>
                <span class="chevron ${this._expanded ? 'expanded' : ''}"></span>
            </div>
        `;
    }

    _renderDetailPanel() {
        return html`
            <div class="detail-panel ${this._expanded ? 'open' : ''}">
                <ki-ds-pwa-alarm-bar
                    .alarmConfig=${this.alarmConfig}
                    .eventDefinitionType=${this.alarmStatus?.eventDefinitionType}
                    .latestValue=${this.timeseries.latestValue}
                    .alarmValue=${this.alarmStatus?.value}
                    .classificationTemplate=${this.classificationTemplate}
                ></ki-ds-pwa-alarm-bar>
            </div>
        `;
    }

    _formatValue(ts) {
        if (isNil(ts.latestValue)) return '---';
        return this.parameters?.computeLastValuePrecision(ts, ts.latestValue) ?? ts.latestValue.toFixed(2);
    }

    _formatDate(dateString) {
        return formatDateTime(dateString) || '---';
    }
}
